/**
 * Reload once when a lazy page's chunk is gone after a deploy.
 *
 * Every deploy renames the hashed chunks and removes the previous build's. A tab
 * opened before the deploy still asks for the old names when it navigates to a lazy
 * page, the import fails ("Failed to fetch dynamically imported module"), and React
 * Router shows its error page. Vite reports exactly this through the
 * `vite:preloadError` event — its documented remedy is to reload, which fetches the
 * current index.html and its current chunks
 * (https://vite.dev/guide/build#load-error-handling).
 *
 * At most one reload per RELOAD_WINDOW_MS, recorded in sessionStorage: if the chunk
 * is still missing straight after a reload (a genuinely broken deploy, or a stale
 * cached index.html), the error surfaces instead of the tab reloading forever. When
 * storage is unavailable the attempt cannot be recorded, so it does not reload.
 */
const STORAGE_KEY = "biteg:staleDeployReloadAt";
const RELOAD_WINDOW_MS = 10_000;

type ReloadWindow = Pick<Window, "addEventListener" | "sessionStorage"> & {
  location: Pick<Location, "reload">;
};

export function installStaleDeployReload(win: ReloadWindow = window): void {
  win.addEventListener("vite:preloadError", () => {
    try {
      const last = Number(win.sessionStorage.getItem(STORAGE_KEY)) || 0;
      if (Date.now() - last < RELOAD_WINDOW_MS) return; // just reloaded: let it surface
      win.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      return; // storage blocked: can't guard against a loop, so don't reload
    }
    win.location.reload();
  });
}
